/*
 * Copyright 2026 Bytefinger Technology (HK) Co., Limited (https://bytefinger.ai)
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { I18n } from '@adp-arch/i18n';

import { type FunctionCallMessageUnit } from '../../../utils/fucntion-call/types';
import { isToolResponseFailed } from './tool-status';

const OUTPUT_PREVIEW_LIMIT = 8000;
type ExecutionState =
  | 'running'
  | 'completed'
  | 'failed'
  | 'cancelled'
  | 'partial'
  | 'blocked';
function resultObject(content?: string): Record<string, unknown> {
  try {
    const value = JSON.parse(content ?? '{}');
    return value && typeof value === 'object' ? value : {};
  } catch {
    return {};
  }
}
function executionState(
  unit: FunctionCallMessageUnit,
  result: Record<string, unknown>,
): ExecutionState {
  if (result.state === 'cancelled') {
    return 'cancelled';
  }
  if (
    isToolResponseFailed(unit.apiResponse?.extra_info) ||
    result.ok === false ||
    result.state === 'failed' ||
    (typeof result.exit_code === 'number' && result.exit_code !== 0)
  ) {
    return 'failed';
  }
  return !unit.apiResponse ||
    ['running', 'starting'].includes(String(result.state))
    ? 'running'
    : 'completed';
}
export function harnessExecutionRows(
  units: FunctionCallMessageUnit[],
  terminated = false,
) {
  const jobs = new Map<
    string,
    { result: Record<string, unknown>; unit: FunctionCallMessageUnit }
  >();
  for (const unit of units) {
    const result = resultObject(unit.apiResponse?.content);
    if (typeof result.job_id === 'string') {
      jobs.set(result.job_id, { result, unit });
    }
  }
  return units.flatMap(unit => {
    const parsed = resultObject(unit.llmOutput.content);
    const call = (parsed.function ?? parsed) as Record<string, unknown>;
    if (typeof call.name !== 'string' || call.name === 'write_todos') {
      return [];
    }
    const result = resultObject(unit.apiResponse?.content);
    if (call.name === 'delegate_tasks' && Array.isArray(result.tasks)) {
      return result.tasks.map((task: Record<string, unknown>) => ({
        name: `Ultra · ${String(task.name ?? 'Worker')}`,
        input:
          typeof call.arguments === 'string'
            ? call.arguments
            : JSON.stringify(call.arguments),
        output: [
          task.output,
          task.error,
          task.details_ref
            ? `Evidence: ${String(task.details_ref)} (context_read)`
            : '',
          task.sources ? `Sources: ${JSON.stringify(task.sources)}` : '',
          task.artifacts ? `Files: ${JSON.stringify(task.artifacts)}` : '',
          task.unfinished
            ? `Remaining: ${JSON.stringify(task.unfinished)}`
            : '',
          task.delivery ? `Delivery: ${String(task.delivery)}` : '',
          Array.isArray(task.tools) && task.tools.length
            ? JSON.stringify(task.tools, null, 2)
            : '',
        ]
          .filter(Boolean)
          .join('\n\n'),
        state: (task.state === 'partial'
          ? 'partial'
          : task.state === 'blocked'
            ? 'blocked'
            : task.state === 'completed'
              ? 'completed'
              : task.state === 'cancelled'
                ? 'cancelled'
                : 'failed') as ExecutionState,
      }));
    }
    const latest =
      typeof result.job_id === 'string' ? jobs.get(result.job_id) : undefined;
    const state = executionState(
      latest?.unit ?? unit,
      latest?.result ?? result,
    );
    return [
      {
        name: call.name,
        input:
          typeof call.arguments === 'string'
            ? call.arguments
            : JSON.stringify(call.arguments),
        output: latest?.unit.apiResponse?.content ?? unit.apiResponse?.content,
        state:
          terminated && state === 'running' ? ('cancelled' as const) : state,
      },
    ];
  });
}
const statusText = () => ({
  running: I18n.t('harness_execution_running'),
  completed: I18n.t('harness_execution_completed'),
  failed: I18n.t('harness_execution_failed'),
  cancelled: I18n.t('harness_execution_cancelled'),
  partial: I18n.t('harness_execution_partial'),
  blocked: I18n.t('harness_execution_blocked'),
});
const symbols = {
  completed: '✓',
  failed: '!',
  cancelled: '−',
  running: '…',
  partial: '!',
  blocked: '!',
};
export function HarnessExecution({
  units,
  terminated = false,
}: {
  units: FunctionCallMessageUnit[];
  terminated?: boolean;
}) {
  const rows = harnessExecutionRows(units, terminated);
  const labels = statusText();
  if (!rows.length) {
    return null;
  }
  return (
    <section
      className="mb-3 rounded-lg border coz-stroke-primary p-3 coz-bg-plus"
      aria-label={I18n.t('harness_execution_title')}
    >
      {rows.map((row, index) => (
        <details key={`${index}:${row.name}`} className="text-xs py-1">
          <summary className="cursor-pointer flex items-center gap-2">
            <span aria-label={labels[row.state]}>{symbols[row.state]}</span>
            <span className="min-w-0 truncate">{row.name}</span>
            <span className="coz-fg-secondary ml-auto">
              {labels[row.state]}
            </span>
          </summary>
          <pre className="whitespace-pre-wrap break-all max-h-48 overflow-auto">
            {row.input}
          </pre>
          {row.output ? (
            <pre className="whitespace-pre-wrap break-all max-h-48 overflow-auto">
              {row.output.slice(0, OUTPUT_PREVIEW_LIMIT)}
            </pre>
          ) : null}
        </details>
      ))}
    </section>
  );
}

// Applies only to the reserved Better Harness delegation tool. Other agent modes
// retain their existing plugin_status/tool_status completion checks.
export function delegationHasIncompleteResult(
  unit: FunctionCallMessageUnit,
): boolean {
  const parsed = resultObject(unit.llmOutput.content);
  const call = (parsed.function ?? parsed) as Record<string, unknown>;
  if (call.name !== 'delegate_tasks') {
    return false;
  }
  const result = resultObject(unit.apiResponse?.content);
  return (
    Array.isArray(result.tasks) &&
    result.tasks.some(
      (task: Record<string, unknown>) => task.state !== 'completed',
    )
  );
}
